import React from "react";
import Link from "next/link";
import Icon from "./Icon";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const footerStyles = `
.t26-footer {
  margin-top: clamp(40px, 6vw, 80px);
  background: color-mix(in oklab, var(--ink) 96%, transparent);
  color: color-mix(in oklab, var(--surface) 88%, var(--ink) 12%);
  padding: clamp(40px, 6vw, 72px) 0 24px;
}
.t26-footer__inner {
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: clamp(16px, 4vw, 32px);
}
.t26-footer__top {
  display: grid;
  /* auto-fit responsive : marche avec 2, 3 ou 4 colonnes selon ce qui
     est disponible. La colonne marque est plus large (clamp jusqu'à 1.5×). */
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: clamp(24px, 4vw, 48px);
  align-items: start;
}
.t26-footer__top > .t26-footer__col--brand {
  /* La colonne marque peut occuper jusqu'à 2 colonnes si l'espace le permet */
  grid-column: span 1;
  min-width: 0;
}
@media (min-width: 980px) {
  .t26-footer__top > .t26-footer__col--brand { grid-column: span 2; }
}

.t26-footer__brand {
  display: inline-flex; align-items: center; gap: 12px;
  margin-bottom: 14px;
}
.t26-footer__brand img {
  width: 48px; height: 48px;
  border-radius: 10px; object-fit: cover;
  background: var(--surface);
}
.t26-footer__brand-name {
  font-family: var(--font-display);
  font-weight: 700; font-size: 20px;
  color: color-mix(in oklab, white 90%, var(--ink) 10%);
}
.t26-footer__tagline {
  font-size: 14px; line-height: 1.55;
  color: color-mix(in oklab, white 65%, var(--ink) 35%);
  max-width: 280px;
}

/* Sélecteur scopé pour battre la règle globale .theme-2026 h1/h2/h3/h4 = ink */
.theme-2026 .t26-footer__col-title,
.t26-footer__col-title {
  font-family: var(--font-display);
  font-size: 11px; font-weight: 700;
  letter-spacing: .22em; text-transform: uppercase;
  color: var(--secondary);
  margin: 0 0 14px;
}
.t26-footer__list {
  display: flex; flex-direction: column; gap: 10px;
  list-style: none; padding: 0; margin: 0;
}
/* Même style pour tous les items : liens cliquables ET texte simple (adresse) */
.t26-footer__list a,
.t26-footer__list-item {
  font-size: 14px;
  color: color-mix(in oklab, white 80%, var(--ink) 20%);
  display: inline-flex; align-items: flex-start; gap: 8px;
  line-height: 1.5;
  text-decoration: none;
  transition: color .15s ease;
}
.t26-footer__list a:hover { color: color-mix(in oklab, white 100%, var(--ink) 0%); }
.t26-footer__list svg { flex-shrink: 0; margin-top: 2px; }
.t26-footer__social { text-decoration: none; }
.t26-footer__bottom a { color: inherit; text-decoration: none; }
.t26-footer__bottom a:hover { color: var(--secondary); }

.t26-footer__socials {
  display: flex; flex-wrap: wrap; gap: 8px;
  margin-top: 10px;
}
.t26-footer__social {
  width: 36px; height: 36px;
  border-radius: 50%;
  border: 1px solid color-mix(in oklab, white 14%, transparent);
  background: color-mix(in oklab, white 6%, transparent);
  color: color-mix(in oklab, white 85%, var(--ink) 15%);
  display: inline-flex; align-items: center; justify-content: center;
  transition: transform .15s ease, border-color .15s ease;
}
.t26-footer__social:hover {
  transform: translateY(-2px);
  border-color: var(--secondary);
  color: var(--secondary);
}

.t26-footer__divider {
  margin: clamp(28px, 4vw, 40px) 0 16px;
  height: 1px; border: 0;
  background: color-mix(in oklab, white 10%, transparent);
}

.t26-footer__bottom {
  display: flex; flex-wrap: wrap; gap: 12px;
  justify-content: space-between;
  font-size: 12px;
  color: color-mix(in oklab, white 60%, var(--ink) 40%);
}
.t26-footer__powered { display: inline-flex; align-items: center; gap: 4px; }
.t26-footer__powered a { color: var(--secondary); font-weight: 700; text-decoration: none; }
`;

function buildFooterLinks(org, config) {
    const links = [];
    // Pages déclarées comme "menu" dans org.pages
    (org.pages || []).forEach(p => {
        if (p?.url && p?.label) links.push({ label: p.label, url: org.BASE_WEB_URL + p.url });
    });
    return links;
}

export default function Footer({ context }) {
    const { org, config } = context;
    if (context.render === "embed") return null;

    const year = new Date().getFullYear();
    const usefulLinksDisabled = !!config["footer.usefullinks.disable"];
    const links = usefulLinksDisabled ? [] : buildFooterLinks(org, config);
    const hideUnisoft = !!config["footer.hide.unisoft"];

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: footerStyles }} />
            <footer className="t26-footer">
                <div className="t26-footer__inner">
                    <div className="t26-footer__top">
                        {/* Colonne 1 : marque + tagline (occupe 2 colonnes en desktop) */}
                        <div className="t26-footer__col--brand">
                            <div className="t26-footer__brand">
                                {org.logo && <img src={org.logo} alt={org.name} />}
                                <span className="t26-footer__brand-name">{org.name}</span>
                            </div>
                            {org.description ? (
                                <p className="t26-footer__tagline">{org.description}</p>
                            ) : org.adresse ? (
                                <p className="t26-footer__tagline">
                                    {org.adresse}{org.ville ? `, ${org.ville}` : ""}
                                </p>
                            ) : null}

                            <div className="t26-footer__socials">
                                {config["social.whatsapp"] && (
                                    <a className="t26-footer__social" href={config["social.whatsapp"]} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                                        <Icon name="whatsapp" size={18} />
                                    </a>
                                )}
                                {config["social.insta"] && (
                                    <a className="t26-footer__social" href={config["social.insta"]} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                        <Icon name="instagram" size={18} />
                                    </a>
                                )}
                                {config["social.fb"] && (
                                    <a className="t26-footer__social" href={config["social.fb"]} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                        <Icon name="facebook" size={18} />
                                    </a>
                                )}
                                {config["social.yt"] && (
                                    <a className="t26-footer__social" href={config["social.yt"]} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                        {renderIcon({ name: "IconBrandYoutube", source: "tabler" }, 18)}
                                    </a>
                                )}
                                {config["social.tiktok"] && (
                                    <a className="t26-footer__social" href={config["social.tiktok"]} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                        {renderIcon({ name: "IconBrandTiktok", source: "tabler" }, 18)}
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Colonne 2 : contact */}
                        <div>
                            <h4 className="t26-footer__col-title"><Trans>Contact</Trans></h4>
                            <ul className="t26-footer__list">
                                {org.contact?.tel && (
                                    <li>
                                        <a href={`tel:${org.contact.tel}`}>
                                            <Icon name="phone" size={14} />
                                            {org.contact.tel}
                                        </a>
                                    </li>
                                )}
                                {org.contact?.email && (
                                    <li>
                                        <a href={`mailto:${org.contact.email}`}>
                                            <Icon name="mail" size={14} />
                                            {org.contact.email}
                                        </a>
                                    </li>
                                )}
                                {org.adresse && (
                                    <li>
                                        <span className="t26-footer__list-item">
                                            <Icon name="mapPin" size={14} />
                                            <span>{org.adresse}{org.ville ? `, ${org.ville}` : ""}</span>
                                        </span>
                                    </li>
                                )}
                            </ul>
                        </div>

                        {/* Colonne 3 : liens utiles */}
                        {links.length > 0 && (
                            <div>
                                <h4 className="t26-footer__col-title"><Trans>Liens utiles</Trans></h4>
                                <ul className="t26-footer__list">
                                    {links.slice(0, 6).map((l, i) => (
                                        <li key={i}>
                                            <Link href={l.url} legacyBehavior><a>{l.label}</a></Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Colonne 4 : accès rapide */}
                        <div>
                            <h4 className="t26-footer__col-title"><Trans>Accès</Trans></h4>
                            <ul className="t26-footer__list">
                                {config["menu.don.active"] !== false && (
                                    <li>
                                        <Link href={org.BASE_WEB_URL + "/don"} legacyBehavior>
                                            <a><Icon name="heart" size={14} /><Trans>Faire un don</Trans></a>
                                        </Link>
                                    </li>
                                )}
                                {config["menu.event.active"] !== false && (
                                    <li>
                                        <Link href={org.BASE_WEB_URL + "/evenements"} legacyBehavior>
                                            <a><Trans>Événements</Trans></a>
                                        </Link>
                                    </li>
                                )}
                                {config["menu.horaire.active"] !== false && (
                                    <li>
                                        <Link href={org.BASE_WEB_URL + "/horaires"} legacyBehavior>
                                            <a><Trans>Horaires</Trans></a>
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>

                    <hr className="t26-footer__divider" />

                    <div className="t26-footer__bottom">
                        <div>
                            © {year} {org.name}
                            {" · "}
                            <a href="/docs/CGU.pdf" target="_blank" rel="noopener noreferrer"><Trans>CGU</Trans></a>
                            {" · "}
                            <a href="/docs/Politique-de-Confidentialité.pdf" target="_blank" rel="noopener noreferrer"><Trans>Confidentialité</Trans></a>
                        </div>
                        {!hideUnisoft && (
                            <div className="t26-footer__powered">
                                <Trans>Propulsé par</Trans>{" "}
                                <a href="https://unisoft.world" target="_blank" rel="noopener noreferrer">Unisoft.world</a>
                            </div>
                        )}
                    </div>
                </div>
            </footer>
        </>
    );
}
